<template>
  <div
    id="app"
    class="h-100"
  >
    <LayoutContainer :layout="layout" :scrollable-to-top="enableScrollToTop"></LayoutContainer>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { mapState } from 'vuex'
import { $themeColors, $themeBreakpoints, $themeConfig } from '@theme-config'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useLayoutConfig from '@hw-ui/layouts/composables/use-layout-config'
import { useWindowSize, useCssVar, useFavicon } from '@vueuse/core'
import store from '@/shared/store'
import hwAdminMenuItems from '@/shared/store/states/menu-items'
import './shared/listeners/shifts'

const LayoutContainer = () => import('@hw-ui/layouts/layout-container/LayoutContainer.vue')

export default {
  components: {
    LayoutContainer,
  },
  data() {
    return {
      enableScrollToTop: $themeConfig.layout.enableScrollToTop
    }
  },
  // TODO We can move this computed: layout once we get to use Vue 3. Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    ...mapState('hwUiLayout', {
      layout: 'type'
    }),
  },
  watch: {
    $route(to) { // TODO move this logic to store and router.beforeEach
      const rootPathTo = to.path.split('/')[1]
      const currentApp = rootPathTo === 'hw-ui' ? 'hw-ui' : 'hw-admin'

      // override hwUiLayout.type if meta.layout is defined
      if(this.$route.meta.layout && this.$route.meta.layout !== this.$store.state.hwUiLayout.type){ 
        this.$store.commit('hwUiLayout/UPDATE_LAYOUT_TYPE', this.$route.meta.layout)
      }

      // switch to new app
      if(this.$store.state.app.name !== currentApp){ 
        this.$store.commit('app/UPDATE_APP_NAME', currentApp)
        this.$store.dispatch('updateLayoutAndMenu', {app: this.$store.state.app.name, layout: this.$store.state.hwUiLayout.type})
      }
    },
  },
  async beforeCreate() {
    await this.$store.dispatch('app/getVersionData')
    if (this.$store.state.appAccount.selectedAccount) {
      document.title = `${this.$store.state.app.appConfig.name} - ${this.$store.state.appAccount.selectedAccount.label}`
    }
    const icon = useFavicon()
    // eslint-disable-next-line global-require, import/no-dynamic-require
    icon.value = require(`../libs/hw-ui/assets/images/logo/${this.$store.state.app.appConfig.type === 'hw-admin' ? 'hoiwa_logo.png' : 'oiwa_logo.png'}`)
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    // register and use hwAdminMenuItems on app setup
    store.commit('hwUiMenu/SET_MENU_ITEMS', {'hw-admin': hwAdminMenuItems})
    store.commit('hwUiMenu/SET_ACTIVE_MENU_ITEMS', {app: store.state.app.name, layout: store.state.hwUiLayout.type})

    const { skin, skinClasses } = useLayoutConfig()
   
    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {skinClasses}
  },
}
</script>

<style type="text/css">
  @font-face{
      font-family:"Hoiwa Logo"; /* Paralucent W00 Demi Bold */
      src:url("../libs/hw-ui/assets/fonts/6029080/4f53693e-7b7c-4c23-9c83-2f2c34c00397.woff2") format("woff2"),
      url("../libs/hw-ui/assets/fonts/6029080/ec4abd87-b5a8-437a-9d51-43dca080b28d.woff") format("woff");
  }
</style>
